import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SOS() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0); // 0 = urgency assessment
  const [urgencyLevel, setUrgencyLevel] = useState(null); // 1, 2, or 3
  const [needType, setNeedType] = useState('');
  const [breathCount, setBreathCount] = useState(0);
  const [breathPhase, setBreathPhase] = useState('ready'); // ready, in, hold, out
  const [grounding, setGrounding] = useState({
    see: ['', '', '', '', ''],
    touch: ['', '', '', ''],
    hear: ['', '', ''],
    smell: ['', ''],
    taste: ['']
  });

  // Check for direct strategy on component mount
  useEffect(() => {
    if (location.state?.directStrategy) {
      setUrgencyLevel(1);
      handleNeedSelect(location.state.directStrategy);
    }
  }, []);

  const urgencyOptions = [
    {
      level: 1,
      label: "I'm having an urge",
      description: "Uncomfortable but manageable",
      color: "#06D6A0",
      icon: "🌊"
    },
    {
      level: 2,
      label: "I'm struggling",
      description: "Strong urge, need support now",
      color: "#FFB627",
      icon: "⚠️"
    },
    {
      level: 3,
      label: "I'm in crisis",
      description: "Overwhelming, need immediate help",
      color: "#EF476F",
      icon: "🆘"
    }
  ];

  const needs = [
    {
      type: 'stress_relief',
      label: 'Stress Relief',
      icon: '🌊',
      description: 'Calm your nervous system'
    },
    {
      type: 'connection',
      label: 'Connection',
      icon: '💙',
      description: 'Reach out for support'
    },
    {
      type: 'distraction',
      label: 'Distraction',
      icon: '⚡',
      description: 'Redirect your energy'
    },
    {
      type: 'grounding',
      label: 'Grounding',
      icon: '🌱',
      description: 'Return to the present'
    }
  ];

  const distractionActivities = [
    'Go for a 10-minute walk',
    'Do 20 pushups or jumping jacks',
    'Call a friend',
    'Work on a hobby',
    'Take a cold shower',
    'Listen to energizing music'
  ];

  const handleUrgencySelect = (level) => {
    setUrgencyLevel(level);
    if (level === 3) {
      // Go directly to crisis resources
      setStep(4);
    } else {
      setStep(1);
    }
  };

  const handleNeedSelect = async (type) => {
    setNeedType(type);
    setStep(2);
    
    // Log SOS use with urgency level
    try {
      await fetch(`${BACKEND_URL}/api/sos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ need_type: type, urgency_level: urgencyLevel })
      });
    } catch (error) {
      console.error('Error logging SOS use:', error);
    }
  };

  const handleOutcome = async (outcome) => {
    if (outcome === 'better') {
      setStep(5); // Show what helped feedback
    } else {
      // Log outcome and show crisis resources
      try {
        await fetch(`${BACKEND_URL}/api/sos`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ need_type: needType, outcome })
        });
      } catch (error) {
        console.error('Error updating SOS outcome:', error);
      }
      setStep(4); // Show crisis resources
    }
  };

  const handleWhatHelped = async (strategy) => {
    try {
      await fetch(`${BACKEND_URL}/api/sos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          need_type: needType, 
          outcome: 'better',
          what_helped: strategy
        })
      });

      toast.success('Thank you for sharing. This helps personalize your experience.');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      console.error('Error saving feedback:', error);
      navigate('/dashboard');
    }
  };

  const startBreathing = () => {
    setBreathPhase('in');
    let count = breathCount;
    
    const breathCycle = () => {
      // In for 4 seconds
      setBreathPhase('in');
      setTimeout(() => {
        // Hold for 7 seconds
        setBreathPhase('hold');
        setTimeout(() => {
          // Out for 8 seconds
          setBreathPhase('out');
          setTimeout(() => {
            count++;
            setBreathCount(count);
            if (count < 3) {
              breathCycle();
            } else {
              setBreathPhase('complete');
              setTimeout(() => setStep(3), 2000);
            }
          }, 8000);
        }, 7000);
      }, 4000);
    };
    
    breathCycle();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1C2127] via-[#2D3640] to-[#1C2127] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Step 0: Urgency Assessment */}
        {step === 0 && (
          <div className="space-y-6" data-testid="sos-urgency">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 bg-[#EF476F]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#EF476F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F8F9FA]">
                How are you feeling?
              </h1>
              <p className="text-base text-[#ADB5BD] leading-relaxed">
                This helps us give you the right support
              </p>
            </div>

            <div className="space-y-4">
              {urgencyOptions.map((option) => (
                <button
                  key={option.level}
                  onClick={() => handleUrgencySelect(option.level)}
                  className="w-full bg-[#2D3640] rounded-2xl border-2 p-5 text-left transition-all active:scale-[0.98] hover:shadow-md"
                  style={{ borderColor: option.color + '40', backgroundColor: option.color + '08' }}
                  data-testid={`urgency-${option.level}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ backgroundColor: option.color + '20' }}
                    >
                      {option.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-[#F8F9FA]">{option.label}</p>
                      <p className="text-sm text-[#ADB5BD]">{option.description}</p>
                    </div>
                    <svg className="w-5 h-5 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
              data-testid="sos-cancel"
            >
              I&apos;m okay, go back
            </Button>
          </div>
        )}

        {/* Step 1: Choose Need */}
        {step === 1 && (
          <div className="space-y-6" data-testid="sos-step1">
            <div className="text-center space-y-2">
              {/* Urgency indicator */}
              {urgencyLevel === 2 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FFB627]/20 text-[#FFB627] rounded-full text-sm font-medium mb-2">
                  <span>⚠️</span> Intensive Support Mode
                </div>
              )}
              <div className="w-16 h-16 bg-[#3A4550] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#F8F9FA]">
                What do you need right now?
              </h1>
              <p className="text-base text-[#ADB5BD] leading-relaxed">
                Choose what feels right
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {needs.map((need) => (
                <button
                  key={need.type}
                  onClick={() => handleNeedSelect(need.type)}
                  className="bg-[#2D3640] rounded-2xl border border-[#4A5568] p-6 text-left hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                  data-testid={`need-${need.type}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="text-4xl">{need.icon}</span>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-[#F8F9FA]">{need.label}</h3>
                      <p className="text-sm text-[#ADB5BD]">{need.description}</p>
                    </div>
                    <svg className="w-6 h-6 text-[#6B7280]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
            >
              Go back
            </Button>
          </div>
        )}

        {/* Step 2: Interventions */}
        {step === 2 && (
          <div className="space-y-6">
            {/* Stress Relief - Breathing */}
            {needType === 'stress_relief' && (
              <div className="text-center space-y-6" data-testid="stress-relief-content">
                <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                  Let's calm your nervous system
                </h2>
                <p className="text-base text-[#ADB5BD]">
                  4-7-8 breathing technique
                </p>

                {breathPhase === 'ready' && (
                  <div className="space-y-6">
                    <div className="bg-[#2D3640] rounded-2xl p-8 space-y-4">
                      <p className="text-base text-[#F8F9FA]">
                        This will help activate your parasympathetic nervous system (your body's natural calming response).
                      </p>
                      <ul className="text-left space-y-2 text-sm text-[#ADB5BD]">
                        <li>• Breathe in through your nose for 4 seconds</li>
                        <li>• Hold your breath for 7 seconds</li>
                        <li>• Exhale slowly through your mouth for 8 seconds</li>
                        <li>• Repeat 3 times</li>
                      </ul>
                    </div>
                    <Button
                      onClick={startBreathing}
                      className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
                      data-testid="start-breathing-btn"
                    >
                      Start Breathing Exercise
                    </Button>
                  </div>
                )}

                {breathPhase !== 'ready' && breathPhase !== 'complete' && (
                  <div className="bg-[#2D3640] rounded-2xl p-12 space-y-6">
                    <div className="relative w-32 h-32 mx-auto">
                      <div className={`absolute inset-0 rounded-full bg-[#00B4D8] transition-all duration-1000 ${
                        breathPhase === 'in' ? 'scale-100' :
                        breathPhase === 'hold' ? 'scale-100' :
                        'scale-75'
                      } opacity-20`} />
                      <div className={`absolute inset-0 rounded-full bg-[#00B4D8] transition-all ${
                        breathPhase === 'in' ? 'scale-100 duration-[4000ms]' :
                        breathPhase === 'hold' ? 'scale-100 duration-[7000ms]' :
                        'scale-75 duration-[8000ms]'
                      } opacity-40`} />
                    </div>
                    <div className="space-y-2">
                      <p className="text-3xl font-bold text-[#00B4D8]">
                        {breathPhase === 'in' && 'Breathe In...'}
                        {breathPhase === 'hold' && 'Hold...'}
                        {breathPhase === 'out' && 'Breathe Out...'}
                      </p>
                      <p className="text-sm text-[#ADB5BD]">
                        Cycle {breathCount + 1} of 3
                      </p>
                    </div>
                  </div>
                )}

                {breathPhase === 'complete' && (
                  <div className="bg-[#2D3640] rounded-2xl p-8 space-y-4">
                    <div className="text-4xl mb-4">✨</div>
                    <p className="text-lg font-medium text-[#F8F9FA]">
                      Breathing exercise complete
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Connection */}
            {needType === 'connection' && (
              <div className="space-y-6" data-testid="connection-content">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                    You're not alone in this
                  </h2>
                  <p className="text-base text-[#ADB5BD]">
                    Connection can help right now
                  </p>
                </div>

                <div className="bg-[#2D3640] rounded-2xl p-6 space-y-4">
                  <div className="space-y-3">
                    <label className="text-base font-medium text-[#F8F9FA] block">
                      Who could you message right now?
                    </label>
                    <Input
                      placeholder="Name of someone you trust..."
                      className="h-12 rounded-xl"
                      data-testid="connection-input"
                    />
                    <p className="text-sm text-[#ADB5BD]">
                      Even a simple "Hey, thinking of you" can make a difference
                    </p>
                  </div>
                </div>

                <div className="bg-[#3A4550] rounded-2xl p-6 space-y-3">
                  <h3 className="text-base font-semibold text-[#F8F9FA]">
                    Crisis Resources (Available 24/7)
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-[#F8F9FA]">
                      <strong>988 Suicide & Crisis Lifeline</strong><br />
                      Call or text 988
                    </p>
                    <p className="text-[#F8F9FA]">
                      <strong>Crisis Text Line</strong><br />
                      Text HOME to 741741
                    </p>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(3)}
                  className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
                  data-testid="connection-continue-btn"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Distraction */}
            {needType === 'distraction' && (
              <div className="space-y-6" data-testid="distraction-content">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                    Let's redirect your energy
                  </h2>
                  <p className="text-base text-[#ADB5BD]">
                    Choose an activity to engage with
                  </p>
                </div>

                <div className="bg-[#2D3640] rounded-2xl p-6 space-y-3">
                  <p className="text-sm font-medium text-[#ADB5BD] mb-4">
                    Pick something that will fully engage your attention:
                  </p>
                  {distractionActivities.map((activity, index) => (
                    <button
                      key={index}
                      onClick={() => setStep(3)}
                      className="w-full text-left p-4 rounded-xl border border-[#4A5568] hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all"
                      data-testid={`activity-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#3A4550] flex items-center justify-center">
                          <span className="text-sm font-semibold text-[#00B4D8]">{index + 1}</span>
                        </div>
                        <span className="text-base text-[#F8F9FA]">{activity}</span>
                      </div>
                    </button>
                  ))}
                </div>

                <p className="text-sm text-center text-[#ADB5BD] italic">
                  The goal is to shift your focus and give your brain something else to do
                </p>
              </div>
            )}

            {/* Grounding */}
            {needType === 'grounding' && (
              <div className="space-y-6" data-testid="grounding-content">
                <div className="text-center space-y-2">
                  <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                    Let's bring you back to the present
                  </h2>
                  <p className="text-base text-[#ADB5BD]">
                    The 5-4-3-2-1 grounding technique
                  </p>
                </div>

                <div className="bg-[#2D3640] rounded-2xl p-6 space-y-6">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                        Name 5 things you can SEE:
                      </h3>
                      <p className="text-sm text-[#ADB5BD] mb-3">Look around you right now</p>
                      {grounding.see.map((_, i) => (
                        <Input
                          key={`see-${i}`}
                          placeholder={`Thing ${i + 1}...`}
                          className="h-10 rounded-xl mb-2"
                          data-testid={`see-${i}`}
                        />
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                        Name 4 things you can TOUCH:
                      </h3>
                      {grounding.touch.map((_, i) => (
                        <Input
                          key={`touch-${i}`}
                          placeholder={`Thing ${i + 1}...`}
                          className="h-10 rounded-xl mb-2"
                          data-testid={`touch-${i}`}
                        />
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                        Name 3 things you can HEAR:
                      </h3>
                      {grounding.hear.map((_, i) => (
                        <Input
                          key={`hear-${i}`}
                          placeholder={`Sound ${i + 1}...`}
                          className="h-10 rounded-xl mb-2"
                          data-testid={`hear-${i}`}
                        />
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                        Name 2 things you can SMELL:
                      </h3>
                      {grounding.smell.map((_, i) => (
                        <Input
                          key={`smell-${i}`}
                          placeholder={`Smell ${i + 1}...`}
                          className="h-10 rounded-xl mb-2"
                          data-testid={`smell-${i}`}
                        />
                      ))}
                    </div>

                    <div>
                      <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                        Name 1 thing you can TASTE:
                      </h3>
                      <Input
                        placeholder="Taste..."
                        className="h-10 rounded-xl"
                        data-testid="taste-0"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(3)}
                  className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
                  data-testid="grounding-continue-btn"
                >
                  Continue
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Check-in */}
        {step === 3 && (
          <div className="text-center space-y-6" data-testid="sos-step3">
            <div className="space-y-2">
              <div className="w-16 h-16 bg-[#3A4550] rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                How are you doing now?
              </h2>
              <p className="text-base text-[#ADB5BD]">
                Be honest with yourself
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <Button
                onClick={() => handleOutcome('better')}
                className="button-primary w-full rounded-full px-8 py-6 h-auto text-lg font-medium"
                data-testid="outcome-better"
              >
                Better
              </Button>
              <Button
                onClick={() => handleOutcome('still_struggling')}
                variant="outline"
                className="w-full rounded-full px-8 py-6 h-auto text-lg font-medium border-[#4A5568]"
                data-testid="outcome-struggling"
              >
                Still struggling
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: What Helped Feedback */}
        {step === 5 && (
          <div className="space-y-6" data-testid="what-helped-feedback">
            <div className="text-center space-y-2">
              <div className="text-4xl mb-2">✨</div>
              <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                What helped most right now?
              </h2>
              <p className="text-base text-[#ADB5BD]">
                Quick tap - this helps us personalize for you
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <button
                onClick={() => handleWhatHelped('breathing')}
                className="bg-[#2D3640] border border-[#4A5568] rounded-xl p-4 hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                data-testid="helped-breathing"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😊</span>
                  <span className="text-base font-medium text-[#F8F9FA]">The breathing exercise</span>
                </div>
              </button>

              <button
                onClick={() => handleWhatHelped('talking')}
                className="bg-[#2D3640] border border-[#4A5568] rounded-xl p-4 hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                data-testid="helped-talking"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😊</span>
                  <span className="text-base font-medium text-[#F8F9FA]">Talking it through</span>
                </div>
              </button>

              <button
                onClick={() => handleWhatHelped('physical')}
                className="bg-[#2D3640] border border-[#4A5568] rounded-xl p-4 hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                data-testid="helped-physical"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😊</span>
                  <span className="text-base font-medium text-[#F8F9FA]">Physical activity suggestion</span>
                </div>
              </button>

              <button
                onClick={() => handleWhatHelped('grounding')}
                className="bg-[#2D3640] border border-[#4A5568] rounded-xl p-4 hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                data-testid="helped-grounding"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😊</span>
                  <span className="text-base font-medium text-[#F8F9FA]">Grounding technique</span>
                </div>
              </button>

              <button
                onClick={() => handleWhatHelped('support')}
                className="bg-[#2D3640] border border-[#4A5568] rounded-xl p-4 hover:border-[#00B4D8] hover:bg-[#3A4550]/30 transition-all active:scale-[0.98]"
                data-testid="helped-support"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">😊</span>
                  <span className="text-base font-medium text-[#F8F9FA]">Just having support available</span>
                </div>
              </button>
            </div>

            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
            >
              Skip
            </Button>
          </div>
        )}

        {/* Step 4: Crisis Resources */}
        {step === 4 && (
          <div className="space-y-6" data-testid="crisis-resources">
            <div className="text-center space-y-2">
              {urgencyLevel === 3 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EF476F]/20 text-[#EF476F] rounded-full text-sm font-medium mb-2">
                  <span>🆘</span> Crisis Support
                </div>
              )}
              <h2 className="text-2xl font-semibold text-[#F8F9FA]">
                {urgencyLevel === 3 ? "You deserve support right now" : "It's okay to need more support"}
              </h2>
              <p className="text-base text-[#ADB5BD]">
                These resources are here for you 24/7
              </p>
            </div>

            {/* Safety Check for Crisis Level */}
            {urgencyLevel === 3 && (
              <div className="bg-[#EF476F]/10 border-2 border-[#EF476F]/30 rounded-2xl p-5 space-y-3">
                <p className="text-base text-[#F8F9FA] font-medium">
                  Before anything else - are you safe right now?
                </p>
                <p className="text-sm text-[#ADB5BD]">
                  If you&apos;re thinking about hurting yourself, please reach out to one of these resources immediately. You matter.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Primary Crisis Line */}
              <a 
                href="tel:988" 
                className="block bg-[#00B4D8] text-white rounded-2xl p-6 space-y-2 hover:bg-[#5E7A88] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">
                      988 Suicide & Crisis Lifeline
                    </h3>
                    <p className="text-sm opacity-90">Call or text 988 - Available 24/7</p>
                  </div>
                  <div className="w-12 h-12 bg-[#2D3640]/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                </div>
              </a>

              {/* Crisis Text Line */}
              <a 
                href="sms:741741&body=HOME" 
                className="block bg-[#2D3640] rounded-2xl border-2 border-[#4A5568] p-6 space-y-2 hover:border-[#00B4D8] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-[#F8F9FA]">
                      Crisis Text Line
                    </h3>
                    <p className="text-sm text-[#ADB5BD]">Text HOME to 741741</p>
                  </div>
                  <div className="w-12 h-12 bg-[#3A4550] rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#00B4D8]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                </div>
              </a>

              {/* SAMHSA Helpline */}
              <div className="bg-[#2D3640] rounded-2xl border border-[#4A5568] p-5">
                <h3 className="text-base font-semibold text-[#F8F9FA] mb-2">
                  SAMHSA National Helpline
                </h3>
                <p className="text-sm text-[#ADB5BD] mb-3">
                  Free, confidential, 24/7 treatment referrals and information
                </p>
                <a href="tel:1-800-662-4357" className="text-[#00B4D8] font-medium">
                  1-800-662-4357
                </a>
              </div>

              {/* Encouragement Card */}
              <div className="bg-gradient-to-br from-[#8FAF9A]/20 to-[#6F8C9B]/20 rounded-2xl p-5 text-center">
                <p className="text-[#F8F9FA] font-medium">
                  Reaching out takes courage. You&apos;re doing the right thing.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {urgencyLevel !== 3 && (
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="w-full rounded-full px-6 py-5 h-auto text-base font-medium border-[#4A5568]"
                >
                  Try another technique
                </Button>
              )}
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="w-full rounded-full px-6 py-4 h-auto text-base text-[#ADB5BD]"
              >
                Return to dashboard
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}